// 下载表格生成：按筛选器/指标过滤数据，生成列信息，应用展示值与日期格式，返回表格数据供导出
import { filterNewData, splitDimensionsByLegends } from '../../AIContent/_utils';
import { IMetricInfo, IDimensionInfo } from '../../AIContent/hooks';
import { IAdditionalTableField, IColumn } from '../../AIContent/_type';
import { getMetricDisplayName } from '@/pages/aigc/utils/dataTransform';
import { uniqBy } from 'lodash-es';
import { DATE_FORMAT_MAP, DATE_OR_TIME_DATA_TYPE, SPECIAL_TIME_TYPE } from '../../../../config/constant';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import {
  applyDisplayToDimensions,
  buildAdditionalColumns,
  buildDimensionColumns,
  buildFirstDimensionColumn,
  buildMetricColumns,
} from './tableHelpers';

/**
 * 获取下载表格数据
 * 专门用于下载功能，不生成图表数据，只生成表格数据
 * 支持多选筛选器值（数组）
 */
export const getDownloadTableData = ({
  inputData,
  filterInfoVal,
  xAxis,
  inputMetricsInfo,
  inputLegends: legends = [],
  dimensionInfo,
  metric,
  additional_table_field,
}: {
  inputData: any[];
  filterInfoVal: Record<string, any>; // 支持 string 或 string[]
  xAxis: any[];
  inputMetricsInfo: IMetricInfo[];
  inputLegends: any[];
  dimensionInfo: IDimensionInfo[];
  metric: string;
  additional_table_field?: IAdditionalTableField[];
}) => {
  // 处理 granularity（如果是数组，取第一个用于日期格式化）
  const granularityValue = Array.isArray(filterInfoVal?.granularity)
    ? filterInfoVal.granularity[0]
    : filterInfoVal?.granularity;
  // const dateFormatter = resolveDateFormatter(granularityValue);
  
  // Step 1: 先手动过滤数据（支持多选，区分大小写）
  const preFilteredData = (inputData || []).filter((item) => {
    const match = Object.entries(filterInfoVal).every(([key, value]) => {
      if (value === null || value === undefined || key === 'metrics') return true;
      
      // 支持多选：如果 value 是数组，检查 item[key] 是否在数组中（区分大小写）
      if (Array.isArray(value)) {
        if (value.length === 0) return true;
        const itemValue = item[key];
        const result = value.some((v) => itemValue === v);
        return result;
      }
      
      // 单选：直接比较（区分大小写）
      const itemValue = item[key];
      return itemValue === value;
    });
    return match;
  });
  
  // Step 2: 使用 filterNewData 添加 _display 字段（传入空对象，不做二次过滤）
  const data = filterNewData(preFilteredData, {}, dimensionInfo);

  // Step 3: 检查指标是否存在
  if (!metric) {
    return {
      data: [],
      columnsInfo: [],
    };
  }

  // Step 4: 生成列信息
  let allColumns: IColumn[] = [];
  
  // 4.1 生成 xAxis 列（第一列）- 使用 getDimensionName 获取列头名称
  const firstColumns: IColumn = buildFirstDimensionColumn(xAxis, dimensionInfo, granularityValue, 'download');

  // 4.2 生成指标列 - 优先使用后端名称，然后才是 getMetricDisplayName
  const metricsInfo = inputMetricsInfo?.filter((item) => item?.data_key === metric);
  const metricColumns: IColumn[] = buildMetricColumns(
    metricsInfo || [],
    metric,
    (metricDataKey: string) => getMetricDisplayName(metricDataKey),
    'download'
  );

  // 4.3 生成维度列（legend 维度和其他维度）- 传入完整的 dimensionInfo 以使用 getDimensionName
  const { legendDimensions: legendsColumnsArr, otherDimensions: dimensionColumnsArr } =
    splitDimensionsByLegends(dimensionInfo || [], legends || []);
  const legendsColumns = buildDimensionColumns(legendsColumnsArr, xAxis, granularityValue, dimensionInfo, 'download');
  const dimensionColumns = buildDimensionColumns(dimensionColumnsArr, xAxis, granularityValue, dimensionInfo, 'download');

  // 4.4 生成 additional_table_field 列
  const additionalColumns = buildAdditionalColumns(additional_table_field, 'download');

  // 4.5 合并所有列
  allColumns = [
    firstColumns,
    ...(legendsColumns || []),
    ...(metricColumns || []),
    ...(dimensionColumns || []),
    ...(additionalColumns || []),
  ];

  // Step 5: 处理数据，确保维度列使用 _display 字段的值（与页面表格一致）
  // 将维度列的 origin_name 字段的值替换为 _display 字段的值，这样 excelConversionFormat 就能正确获取格式化后的值
  const processedData = applyDisplayToDimensions(data || [], allColumns);

  // Step 6: 构建 tableInfo
  const tableInfo = {
    data: processedData,
    columnsInfo: uniqBy(allColumns, 'origin_name'),
  };

  // Step 7: 格式化日期列（按行 granularity 格式化，仅处理 DATE_OR_TIME_DATA_TYPE）
  const formattedTableInfo = formatDateColumnsPerRow(tableInfo);
  return {
    data: formattedTableInfo.data,
    columnsInfo: (formattedTableInfo as any).columnsInfo || tableInfo.columnsInfo,
  };
};

// 按行 granularity 格式化日期列，列层面保持文本/固定格式，避免多粒度混用
const formatDateColumnsPerRow = (
  tableInfo: { data: any[]; columnsInfo: IColumn[]; },
) => {
  const dateColumns = (tableInfo.columnsInfo || []).filter((col) =>
    DATE_OR_TIME_DATA_TYPE.includes((col.origin_name || '') as string)
  );

  const formattedData = (tableInfo?.data || []).map((item) => {
    const rowGranularity = item?.granularity?.toLocaleLowerCase();
    // const dateFormatter =
    //   DATE_FORMAT_MAP?.[rowGranularity as keyof typeof DATE_FORMAT_MAP] || rowGranularity || 'day';
    const dateFormatter = SPECIAL_TIME_TYPE?.[rowGranularity as keyof typeof SPECIAL_TIME_TYPE] || DATE_FORMAT_MAP?.[rowGranularity as keyof typeof DATE_FORMAT_MAP] ||  rowGranularity || 'day';

    const next = { ...item };
    dateColumns.forEach((col) => {
      const originKey = col.origin_name;
      const val = item?.[originKey];

      if (val !== undefined && val !== null) {
        next[originKey] = dateTimeFormat(val, dateFormatter);
        next[`${originKey}_display`] = dateTimeFormat(val, dateFormatter);
      }
    });
    return next;
  });

  return {
    ...tableInfo,
    data: formattedData,
  };
};